import { z } from 'zod';

/**
 * Template library (BB-56, CNT-001): versioned templates per channel. Inserting a template copies the content of one
 * version into the message (Braze's copy semantics) and records `{ id, version }` as the message's source, so a later
 * version never changes messages already built. Email templates are out of phase 1 (DEC-015).
 */
export const TEMPLATE_CHANNELS = ['push', 'card', 'iam', 'banner', 'webhook', 'canvas'] as const;
export const TemplateChannel = z.enum(TEMPLATE_CHANNELS);
export type TemplateChannel = z.infer<typeof TemplateChannel>;

/**
 * The content keys of each channel. Message channels hold what a campaign variant holds under the same delivery
 * channel (push: one content per platform, card: the `content_card` channel content, IAM: `in_app`); a banner holds
 * its `banner` pages; a Canvas template holds the graph `definition` without audience (instantiated by BB-110).
 */
export const TEMPLATE_SLOTS = {
  push: ['push_android', 'push_ios'],
  card: ['content_card'],
  iam: ['in_app'],
  banner: ['banner'],
  webhook: ['webhook'],
  canvas: ['definition'],
} as const satisfies Record<TemplateChannel, readonly string[]>;

export const TEMPLATE_NAME_MAX = 200;

/** Content of a version: slot → object (see `TEMPLATE_SLOTS`). */
export const TemplateContent = z.record(z.string().min(1).max(32), z.record(z.string(), z.unknown()));
export type TemplateContent = z.infer<typeof TemplateContent>;

/** «Created from template vN» on a message (campaign variant or Message step), per delivery channel. */
export const TemplateSource = z.strictObject({ id: z.uuid(), version: z.int().min(1) });
export type TemplateSource = z.infer<typeof TemplateSource>;

const Name = z.string().trim().min(1).max(TEMPLATE_NAME_MAX);
const Description = z.string().trim().max(1000);
const Notes = z.string().trim().max(500);

export const TemplateListQuery = z.object({
  channel: TemplateChannel.optional(),
  query: z.string().trim().max(255).optional(),
  tag: z.string().trim().max(64).optional(),
  archived: z.enum(['true', 'false']).default('false'),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TemplateListQuery = z.infer<typeof TemplateListQuery>;

export const CreateTemplateBody = z.strictObject({ channel: TemplateChannel, name: Name, description: Description.optional(), content: TemplateContent, notes: Notes.optional() });
export type CreateTemplateBody = z.infer<typeof CreateTemplateBody>;

export const UpdateTemplateBody = z
  .strictObject({ name: Name.optional(), description: Description.nullable().optional() })
  .refine((b) => Object.keys(b).length > 0, 'nothing to change');
export type UpdateTemplateBody = z.infer<typeof UpdateTemplateBody>;

/** A new version; `baseVersion` is the version the editor started from (409 `template_version_conflict` when another one was saved since). */
export const AddTemplateVersionBody = z.strictObject({ content: TemplateContent, notes: Notes.optional(), baseVersion: z.int().min(1).optional() });
export type AddTemplateVersionBody = z.infer<typeof AddTemplateVersionBody>;

export const DuplicateTemplateBody = z.strictObject({ name: Name.optional() });
export type DuplicateTemplateBody = z.infer<typeof DuplicateTemplateBody>;

/** «Save as template»: a stored message — the working draft, else the published content. */
export const MessageRef = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('campaign'), campaignId: z.uuid(), variantApiId: z.string().min(1).max(64) }),
  z.strictObject({ type: z.literal('canvas_step'), canvasId: z.uuid(), stepId: z.string().min(1).max(128) }),
]);
export type MessageRef = z.infer<typeof MessageRef>;

export const TemplateFromMessageBody = z.strictObject({ channel: TemplateChannel.exclude(['canvas']), name: Name, description: Description.optional(), messageRef: MessageRef });
export type TemplateFromMessageBody = z.infer<typeof TemplateFromMessageBody>;

/** Copies one version (default: the current one). */
export const InstantiateTemplateBody = z.strictObject({ version: z.int().min(1).optional() });
export type InstantiateTemplateBody = z.infer<typeof InstantiateTemplateBody>;

export interface TemplateIssue {
  severity: 'error' | 'warning';
  code: string;
  /** `<slot>.<path in the slot content>`. */
  path: string;
  message: string;
}

export interface TemplatePersonRef {
  id: string;
  email: string | null;
}

export interface TemplateSummary {
  id: string;
  apiId: string;
  channel: TemplateChannel;
  name: string;
  description: string | null;
  currentVersion: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  updatedBy: TemplatePersonRef | null;
  archivedAt: string | null;
}

export interface TemplateVersionView {
  version: number;
  content: TemplateContent;
  notes: string | null;
  createdBy: TemplatePersonRef | null;
  createdAt: string;
}

export interface TemplateUsageView {
  objectType: string;
  objectId: string;
  name: string;
  via: string;
  status?: 'draft' | 'scheduled' | 'active' | 'paused' | 'inactive';
  url?: string;
}

export interface TemplateDetails extends TemplateSummary {
  /** Newest first, without content. */
  versions: Array<Omit<TemplateVersionView, 'content'>>;
  current: TemplateVersionView;
  /** Campaigns and Canvases whose messages were built from this template (any version). */
  usage: TemplateUsageView[];
}

/** Create / new version: the warnings of the channel check (errors answer 422 `template_invalid`). */
export interface TemplateWriteResult extends TemplateDetails {
  warnings: TemplateIssue[];
}

export interface TemplateArchiveResult extends TemplateDetails {
  /** Draft campaigns and Canvases built from it (active ones refuse the archive). */
  warnings: TemplateUsageView[];
}

/** What a composer inserts: a copy of the version's content and the source to record on the message. */
export interface TemplateInstance {
  channel: TemplateChannel;
  name: string;
  content: TemplateContent;
  source: TemplateSource;
}
